import React from 'react';
import { Box, Typography, Grid, Paper, useTheme, Container, Avatar, IconButton, Link } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import { TikTokIcon } from '../components/icons/TikTokIcon';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px) scale(1.02)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '20px',
  border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const GradientSection = styled(Box)(({ theme }) => ({
  borderRadius: '30px',
  padding: theme.spacing(6),
  marginBottom: theme.spacing(6),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
}));

const ProjectCard = styled(Paper)(() => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '20px',
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px) scale(1.02)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
  },
  '&:hover::before': {
    opacity: 1,
  },
  '&:hover .project-content': {
    transform: 'translateY(0)',
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    background: theme.palette.primary.main,
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease-in-out',
}));

const AnimatedContainer = motion(Container);

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';

  const formatText = (text: string) => {
    if (!isRTL) return text;
    return text.replace(/([.!?])(\s|$)/g, '$2$1');
  };

  return (
    <AnimatedContainer 
      maxWidth="lg" 
      sx={{ py: 6 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <Box 
        sx={{ 
          mb: 8, 
          textAlign: 'center',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -20,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100px',
            height: '4px',
            background: theme.palette.primary.main,
            borderRadius: '2px',
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Avatar
            src="https://res.cloudinary.com/dkaie0i1s/image/upload/v1745213182/Screenshot_2025-04-21_at_8.25.34_qy7sks.webp"
            alt="Inbali"
            sx={{
              width: 180,
              height: 180,
              mx: 'auto',
              mb: 3,
              border: '4px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
              },
              '& img': {
                objectFit: 'cover',
                transform: 'scale(1.1)',
              }
            }}
          />
        </motion.div>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            background: theme.palette.primary.main,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          {formatText(t('pages.about.title'))}
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}
        >
          {formatText(t('pages.about.subtitle'))}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Link href="https://www.instagram.com/inbal.artmakers/" target="_blank" rel="noopener noreferrer">
            <SocialButton size="large">
              <InstagramIcon />
            </SocialButton>
          </Link>
          <Link href="https://www.facebook.com/inbalijewelry" target="_blank" rel="noopener noreferrer">
            <SocialButton size="large">
              <FacebookIcon />
            </SocialButton>
          </Link>
          <Link href="https://wa.me/message/RT4MXN3435UGG1" target="_blank" rel="noopener noreferrer">
            <SocialButton size="large">
              <WhatsAppIcon />
            </SocialButton>
          </Link>
          <Link href="https://www.tiktok.com/@inbal.artmakers?_t=ZS-8vbx2aglBkG&_r=1" target="_blank" rel="noopener noreferrer">
            <SocialButton size="large">
              <TikTokIcon />
            </SocialButton>
          </Link>
        </Box>
      </Box>

      {/* Mission & Vision Section */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        <Grid 
          item 
          xs={12} 
          md={6}
          sx={{ 
            order: { xs: 1, md: isRTL ? 2 : 1 }
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <StyledPaper>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.8,
                  direction: isRTL ? 'rtl' : 'ltr',
                  unicodeBidi: 'embed'
                }}
              >
                {formatText(t('pages.about.mission'))}
              </Typography>
            </StyledPaper>
          </motion.div>
        </Grid>
        <Grid 
          item 
          xs={12} 
          md={6}
          sx={{ 
            order: { xs: 2, md: isRTL ? 1 : 2 }
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <StyledPaper>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.8,
                  direction: isRTL ? 'rtl' : 'ltr',
                  unicodeBidi: 'embed'
                }}
              >
                {formatText(t('pages.about.vision'))}
              </Typography>
            </StyledPaper>
          </motion.div>
        </Grid>
      </Grid>

      {/* Values Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <GradientSection>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '800px',
              mx: 'auto',
              textAlign: 'center',
              lineHeight: 1.8,
              direction: isRTL ? 'rtl' : 'ltr',
              unicodeBidi: 'embed'
            }}
          >
            {formatText(t('pages.about.values'))}
          </Typography>
        </GradientSection>
      </motion.div>

      {/* Projects Section */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            textAlign: 'center',
            mb: 6,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <span style={{ fontSize: '2.5rem' }}>🎨</span>
          {formatText(t('pages.about.projects.title'))}
        </Typography>
        <Grid container spacing={4}>
          {[1, 2, 3, 4].map((index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 * index }}
              >
                <ProjectCard>
                  <Box
                    component="img"
                    src={`/images/project${index}.jpg`}
                    alt={`Project ${index}`}
                    sx={{
                      width: '100%',
                      height: 300,
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    className="project-content"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 3,
                      color: 'white',
                      transform: 'translateY(100%)',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {formatText(t(`pages.about.projects.project${index}.title`))}
                    </Typography>
                    <Typography variant="body2">
                      {formatText(t(`pages.about.projects.project${index}.description`))}
                    </Typography>
                  </Box>
                </ProjectCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Contact Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <GradientSection>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '600px',
              mx: 'auto',
              textAlign: 'center',
              lineHeight: 1.8,
              direction: isRTL ? 'rtl' : 'ltr',
              unicodeBidi: 'embed'
            }}
          >
            {formatText(t('pages.about.contact.description'))}
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link 
              href="mailto:contact@inbalart.co.il"
              sx={{ 
                mr: 2,
                display: 'inline-block',
                verticalAlign: 'middle',
              }}
            >
              <SocialButton size="large">
                <EmailIcon />
              </SocialButton>
            </Link>
            <Typography 
              variant="body1" 
              component="span"
              sx={{ 
                direction: isRTL ? 'rtl' : 'ltr',
                unicodeBidi: 'embed',
                verticalAlign: 'middle',
                display: 'inline-block',
                ml: 2,
              }}
            >
              {formatText(t('pages.about.contact.email'))}:{' '}
              <a 
                href="mailto:contact@inbalart.co.il"
                style={{ 
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 500
                }}
              >
                contact@inbalart.co.il
              </a>
            </Typography>
          </Box>
        </GradientSection>
      </motion.div>
    </AnimatedContainer>
  );
}; 